import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { useFM } from '@/context/FMContext';
import { toast } from 'sonner';
import { AlertTriangle, Clock, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const SEVERITY_COLORS = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200',
};

const STATUS_CONFIG = {
  open: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50', label: 'Open' },
  in_progress: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-50', label: 'In Progress' },
  resolved: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', label: 'Resolved' },
  closed: { icon: XCircle, color: 'text-gray-500', bg: 'bg-gray-50', label: 'Closed' },
};

function SummaryCard({ label, count, color }) {
  return (
    <div className={`rounded-lg p-3 text-center ${color}`}>
      <div className="text-2xl font-bold">{count}</div>
      <div className="text-xs font-medium mt-0.5">{label}</div>
    </div>
  );
}

function EscalationCard({ escalation, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const statusConf = STATUS_CONFIG[escalation.status] || STATUS_CONFIG.open;
  const StatusIcon = statusConf.icon;

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdating(true);
      await axiosInstance.put(`/fm/escalations/${escalation.escalation_id}`, {
        status: newStatus,
      });
      toast.success(`Escalation marked as ${newStatus.replace('_', ' ')}`);
      onStatusChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className={`rounded-lg border p-4 ${statusConf.bg} transition-all`}>
      <div className="flex items-start justify-between" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <StatusIcon className={`w-4 h-4 ${statusConf.color} flex-shrink-0`} />
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${SEVERITY_COLORS[escalation.severity] || SEVERITY_COLORS.medium}`}>
              {escalation.severity}
            </span>
            <span className="text-xs text-muted-foreground">{escalation.category}</span>
          </div>
          <p className="text-sm font-medium truncate">{escalation.description}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {escalation.gym_name || escalation.gym_id} &middot; {new Date(escalation.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <button className="p-1 text-muted-foreground">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t space-y-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Full Description</p>
            <p className="text-sm">{escalation.description}</p>
          </div>
          {escalation.resolution_notes && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Resolution Notes</p>
              <p className="text-sm">{escalation.resolution_notes}</p>
            </div>
          )}
          {escalation.status !== 'resolved' && escalation.status !== 'closed' && (
            <div className="flex gap-2">
              {escalation.status === 'open' && (
                <button
                  onClick={() => handleStatusChange('in_progress')}
                  disabled={updating}
                  className="flex-1 text-xs py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-medium disabled:opacity-50"
                >
                  Mark In Progress
                </button>
              )}
              <button
                onClick={() => handleStatusChange('resolved')}
                disabled={updating}
                className="flex-1 text-xs py-2 bg-green-500 hover:bg-green-600 text-white rounded-md font-medium disabled:opacity-50"
              >
                Mark Resolved
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FMEscalations() {
  const { selectedGymId } = useFM();
  const [data, setData] = useState({ escalations: [], summary: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchEscalations = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedGymId) params.gym_id = selectedGymId;
      const res = await axiosInstance.get('/fm/escalations', { params });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load escalations');
    } finally {
      setLoading(false);
    }
  }, [selectedGymId]);

  useEffect(() => {
    fetchEscalations();
  }, [fetchEscalations]);

  const filtered = statusFilter === 'all'
    ? data.escalations
    : data.escalations.filter(e => e.status === statusFilter);

  const summary = data.summary || {};

  return (
    <div className="p-4 pb-24 max-w-lg mx-auto">
      <h1 className="text-lg font-bold mb-4">Escalations</h1>

      <div className="grid grid-cols-4 gap-2 mb-4">
        <SummaryCard label="Open" count={summary.open || 0} color="bg-red-50 text-red-700" />
        <SummaryCard label="In Progress" count={summary.in_progress || 0} color="bg-yellow-50 text-yellow-700" />
        <SummaryCard label="Resolved" count={summary.resolved || 0} color="bg-green-50 text-green-700" />
        <SummaryCard label="Closed" count={summary.closed || 0} color="bg-gray-50 text-gray-700" />
      </div>

      <div className="mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status ({data.total})</SelectItem>
            <SelectItem value="open">Open ({summary.open || 0})</SelectItem>
            <SelectItem value="in_progress">In Progress ({summary.in_progress || 0})</SelectItem>
            <SelectItem value="resolved">Resolved ({summary.resolved || 0})</SelectItem>
            <SelectItem value="closed">Closed ({summary.closed || 0})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading escalations...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <AlertTriangle className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">No escalations found</p>
          <p className="text-xs text-muted-foreground mt-1">Use the red button to report issues</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((esc) => (
            <EscalationCard
              key={esc.escalation_id}
              escalation={esc}
              onStatusChange={fetchEscalations}
            />
          ))}
        </div>
      )}
    </div>
  );
}
